import React, { useState, useEffect } from "react";
import { FLOW_TYPES, PAYMENTS_MOD } from "./Constants";
import { useCategoryForm } from '../hooks/useCategoryForm'
import { authFetch } from '../utils/api';

export default function OperationModal({
  show,
  editingOperation,
  currentUser,
  categories,
  accounts,
  persons,
  selectedPerson,
  selectedAccount,
  onSave,
  onCancel,
  onDeleteCallback,
  refreshAllData,
}) {
  const [formData, setFormData] = useState({
    date: "",
    categorie_id: "",
    account_id: "",
    person_id: "",
    statut: "Payé",
    montant: "",
    description: "",
    type: "standard",
    paiementMode: "",
    sourceAccountId: null,
    destinationAccountId: null,
  });

  // 3. Callback pour mettre à jour formData.categorie_id
  const onCategoryCreated = (newCategoryId) => {
      setFormData(prevData => ({ ...prevData, categorie_id: newCategoryId }));
  };
      
  const {
      showCategoryForm,
      newCategoryName,
      newCategoryType,
      setNewCategoryName,
      setNewCategoryType,
      setShowCategoryForm,
      handleCreateCategory,
  } = useCategoryForm(currentUser, onCategoryCreated, refreshAllData);

  // 4. Filtrer les catégories cachées/masquées (pour les dropdowns, on n'affiche que les actives)
  const visibleCategories = categories.filter(c => !c.is_hidden);

  useEffect(() => {
    if (editingOperation) {
// ... reste du code useEffect (inchangé)
      const dateObj = new Date(editingOperation.date);
      const formattedDate = dateObj.toISOString().slice(0, 10);
      
      const isTransfer = editingOperation.sourceAccountId !== null || editingOperation.destinationAccountId !== null;

      setFormData({
        ...editingOperation,
        date: formattedDate,
        type: isTransfer ? 'transfert' : 'standard',
        sourceAccountId: editingOperation.sourceAccountId ? Number(editingOperation.sourceAccountId) : null,
        destinationAccountId: editingOperation.destinationAccountId ? Number(editingOperation.destinationAccountId) : null,
        categorie_id: editingOperation.categorie_id || ""
      });
    } else {
      setFormData({
        date: new Date().toISOString().slice(0, 10),
        categorie_id: "",
        account_id: selectedAccount?.id || "",
        person_id: selectedPerson?.id || "",
        statut: "Payé",
        montant: "",
        description: "",
        type: "standard",
        paiementMode: "",
        sourceAccountId: null,
        destinationAccountId: null,
      });
    }
  }, [editingOperation, selectedPerson, selectedAccount]);

  // ✅ FILTRER LES PERSONNES GÉRÉES (is_managed = true)
  const managedPersons = persons.filter(p => p.is_managed === true || p.is_managed === 1);

  if (!show) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFormData(prevData => {
      return {
        ...prevData,
        type: newType,
        sourceAccountId: newType === "transfert" ? null : null,
        destinationAccountId: newType === "transfert" ? "" : null,
        person_id: newType === "transfert" ? null : prevData.person_id,
        account_id: newType === "transfert" ? null : prevData.account_id,
        paiementMode: newType === "transfert" ? "Virement" : "",
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const isEditing = !!editingOperation?.id;
    const method = isEditing ? "put" : "post";
    const urlPath = isEditing
      ? `/operations/${editingOperation.id}`
      : `/operations`;

    const dataToSave = {
      ...formData,
      person_id: formData.type === 'standard' ? Number(formData.person_id) : null,
      account_id: formData.type === 'standard' ? Number(formData.account_id) : null,
      categorie_id: formData.categorie_id ? Number(formData.categorie_id) : null,
      sourceAccountId: formData.type === 'transfert' ? Number(formData.sourceAccountId) : null,
      destinationAccountId: formData.type === 'transfert' ? Number(formData.destinationAccountId) : null,
      montant: Number(formData.montant),
      statut: formData.statut,
      paiementMode: formData.paiementMode,
    };

    try {
      const response = await authFetch[method](urlPath, dataToSave);

      if (response.ok) {
        // On vérifie s'il y a du contenu avant de parser le JSON
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          await response.json(); 
        }
        
        onSave(); // Rafraîchit les données parentes
        onCancel(); // Ferme le modal
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Erreur lors de la sauvegarde.");
      }
    } catch (error) {
      console.error("Erreur de sauvegarde :", error);
      alert(error.message);
    }
  };

  const handleDelete = async () => {
    if (!editingOperation?.id) return;
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette opération ?")) {
      try {
        const response = await authFetch.delete(`/operations/${editingOperation.id}`);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Erreur lors de la suppression.");
        }

        if (onDeleteCallback) onDeleteCallback();
        else refreshAllData();
        
        onCancel();
      } catch (error) {
        alert(error.message);
      }
    }
  };

  const isTransfer = formData.type === "transfert";
  
  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <h3>{editingOperation ? "Modifier une opération" : "Nouvelle opération"}</h3>
        <form onSubmit={handleSave} style={styles.form}>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            style={styles.input}
            required
          />

          <select
            name="type"
            value={formData.type}
            onChange={handleTypeChange}
            style={styles.input}
          >
            <option value="standard">Standard</option>
            <option value="transfert">Transfert</option>
          </select>
          
          <input
            type="number"
            step="0.01"
            placeholder="Montant"
            name="montant"
            value={formData.montant || ""}
            onChange={handleChange}
            style={styles.input}
            required
            min={isTransfer ? "0" : undefined}
          />

          <input
            type="text"
            placeholder="Description"
            name="description"
            value={formData.description || ""}
            onChange={handleChange}
            style={styles.input}
          />
          
          {/* 9. Affichage conditionnel du sélecteur de catégorie ou du formulaire de création (UNIQUEMENT pour les opérations standards) */}
          {showCategoryForm ? (
                <div style={{...styles.form, padding: '10px', border: '1px solid #ccc', borderRadius: '4px'}}>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}> {/* 💡 NOUVEAU CONTENEUR FLEX */}
                        <input
                            type="text"
                            placeholder="Nom de la nouvelle catégorie"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            style={{...styles.input, flexGrow: 1}} // 💡 INPUT ÉLARGI
                            required
                        />
                        {/* 💡 SÉLECTION DU TYPE */}
                        <select
                            value={newCategoryType}
                            onChange={(e) => setNewCategoryType(e.target.value)}
                            style={{ ...styles.input, width: '120px', flexShrink: 0 }}
                            required
                        >
                            {FLOW_TYPES.map(type => (
                                <option key={type.code} value={type.code}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                        </div>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button 
                            type="button" 
                            onClick={() => {
                                setShowCategoryForm(false);
                                setNewCategoryName('');
                                setNewCategoryType('D');
                            }} 
                            style={styles.cancelSmall}
                        >
                            Annuler
                        </button>
                        <button type="button" onClick={handleCreateCategory} style={styles.addSmall}>
                            Créer
                        </button>
                    </div>
                </div>
            ) : (
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                  <select
                      name="categorie_id"
                      value={formData.categorie_id || ""}
                      onChange={handleChange}
                      style={{...styles.input, flexGrow: 1}}
                      required={!isTransfer}
                  >
                      <option value="">Choisir une catégorie</option>
                      {/* 10. Utiliser les catégories filtrées */}
                      {visibleCategories.map((c) => (
                          <option key={c.id} value={c.id}>
                              {c.name}
                          </option>
                      ))}
                  </select>
                  <button 
                      type="button" 
                      onClick={() => setShowCategoryForm(true)}
                      style={styles.actionSmall} // Nouveau style
                      title="Créer une nouvelle catégorie"
                  >
                      ➕
                  </button>
              </div>
            )
          }

          <select
// ... reste du code jusqu'à la fin du composant (inchangé, sauf styles ajoutés en bas)
            name="statut"
            value={formData.statut || "Payé"}
            onChange={handleChange}
            style={styles.input}
          >
            <option value="Payé">Payé</option>
            <option value="Non payé">Non payé</option>
          </select>

          {!isTransfer && (
            <select
              name="paiementMode"
              value={formData.paiementMode || ""}
              onChange={handleChange}
              style={styles.input}
            >
              {PAYMENTS_MOD.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          )}

          {isTransfer ? (
            <>
              {/* Compte de départ pour les transferts */}
              <select
                name="sourceAccountId"
                value={formData.sourceAccountId || ""}
                onChange={handleChange}
                style={styles.input}
                required
              >
                <option value="">Compte de départ</option>
                {accounts.map((c) => {
                  const person = persons.find((p) => p.id === c.personId);
                  return (
                    <option key={c.id} value={c.id}>
                      {person?.name || "N/A"} - {c.name}
                    </option>
                  );
                })}
              </select>
              {/* Compte d'arrivée pour les transferts */}
              <select
                name="destinationAccountId"
                value={formData.destinationAccountId || ""}
                onChange={handleChange}
                style={styles.input}
                required
              >
                <option value="">Compte d'arrivée</option>
                {accounts
                  .filter((c) => c.id !== Number(formData.sourceAccountId))
                  .map((c) => {
                    const person = persons.find((p) => p.id === c.personId);
                    return (
                      <option key={c.id} value={c.id}>
                        {person?.name || "N/A"} - {c.name}
                      </option>
                    );
                  })}
              </select>
            </>
          ) : (
            <>
              {/* Personne et compte pour les opérations standard */}
              <select
                name="person_id"
                value={formData.person_id || ""}
                onChange={(e) => {
                  const newPersonId = parseInt(e.target.value);
                  setFormData((prevData) => ({
                    ...prevData,
                    person_id: newPersonId,
                    account_id: accounts.find(acc => acc.personId === newPersonId)?.id || ""
                  }));
                }}
                style={styles.input}
                required
              >
                <option value="">Choisir une personne</option>
                {managedPersons.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <select
                name="account_id"
                value={formData.account_id || ""}
                onChange={handleChange}
                style={styles.input}
                required
              >
                <option value="">Choisir un compte</option>
                {accounts.filter(acc => acc.personId === formData.person_id).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </>
          )}

          <div style={styles.modalActions}>
            {editingOperation && (
              <button
                type="button"
                style={styles.delete}
                onClick={handleDelete}
              >
                Supprimer
              </button>
            )}
            <button type="button" style={styles.cancel} onClick={onCancel}>
              Annuler
            </button>
            <button type="submit" style={styles.save}>
              {editingOperation ? "Enregistrer" : "Ajouter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ... styles sont inchangés, ajout de actionSmall et cancelSmall
const styles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    padding: "20px",
    borderRadius: "8px",
    width: "500px",
  },
  form: { display: "flex", flexDirection: "column", gap: "10px" },
  input: {
    padding: "10px",
    borderRadius: "4px",
    border: "1px solid #ccc",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "10px",
    gap: "10px",
  },
  save: {
    background: "#28a745",
    color: "#fff",
    border: "none",
    padding: "10px 15px",
    borderRadius: 4,
  },
  cancel: {
    background: "#6c757d",
    color: "#fff",
    border: "none",
    padding: "10px 15px",
    borderRadius: 4,
  },
  delete: {
    background: "#dc3545",
    color: "#fff",
    border: "none",
    padding: "10px 15px",
    borderRadius: 4,
    marginRight: "auto",
  },
// Nouveaux styles pour les boutons d'action rapide dans le formulaire
  actionSmall: {
    background: '#ff9800',
    color: '#fff',
    border: 'none',
    padding: '10px 10px',
    borderRadius: '4px',
    cursor: 'pointer',
    width: '40px',
    height: '40px',
    alignSelf: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
},
  cancelSmall: {
    background: '#6c757d',
    color: '#fff',
    border: 'none',
    padding: '5px 10px',
    borderRadius: 4,
    cursor: 'pointer',
    marginTop: 0,
},
  addSmall: {
    background: "#007bff",
    color: "#fff",
    border: "none",
    padding: "5px 10px",
    borderRadius: 4,
    cursor: "pointer",
    marginTop: 5,
  },
};